import {
  Button,
  Callout,
  Divider,
  Drawer,
  DrawerSize,
  Icon,
  type IconName,
  Tag,
  Tooltip,
} from "@blueprintjs/core";
import type { PeopleSearchResultItem, RevealedPerson } from "../lib/api";

interface RevealStateLite {
  emailLoading: boolean;
  phoneLoading: boolean;
  email?: string | null;
  emailVerified?: boolean;
  phone?: string | null;
  phoneType?: string | null;
  cached?: boolean;
  pending?: boolean;
  noResult?: { kind: "email" | "phone"; message: string } | null;
}

interface PeopleProfileDrawerProps {
  open: boolean;
  onClose: () => void;
  person: PeopleSearchResultItem | null;
  // Reveal state for this specific person (keyed by the parent), or undefined.
  state?: RevealStateLite;
  // Optional revealed-person record once Apollo has unmasked it (after a reveal).
  revealed?: RevealedPerson | null;
  phoneWebhookEnabled: boolean;
  onReveal: (type: "email" | "phone") => void;
  onCopy: (value: string, label: string) => void;
}

const AVATAR_COLORS = ["#2b95d6", "#634dbf", "#0d8050", "#c87619", "#8f398f", "#1f6b6b", "#96290d", "#5642a6"];
function avatarColor(seed: string) {
  let h = 0;
  for (const c of seed) h = ((h << 5) - h + c.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function locationOf(p: { city?: string | null; state?: string | null; country?: string | null }): string | null {
  const parts = [p.city, p.state, p.country].filter(Boolean) as string[];
  return parts.length ? parts.join(", ") : null;
}

function MetaRow({
  icon,
  label,
  value,
  onClick,
  rightIcon,
}: {
  icon: IconName;
  label: string;
  value: string | null | undefined;
  onClick?: () => void;
  rightIcon?: IconName;
}) {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 0",
        borderBottom: "1px solid #ebf1f5",
      }}
    >
      <Icon icon={icon} size={14} color="#5c7080" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
        <div
          style={{
            fontSize: 13,
            color: "#182026",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: onClick ? "pointer" : "default",
          }}
          onClick={onClick}
          title={value}
        >
          {value}
        </div>
      </div>
      {rightIcon && (
        <Button minimal small icon={rightIcon} onClick={onClick} />
      )}
    </div>
  );
}

export function PeopleProfileDrawer({
  open,
  onClose,
  person,
  state,
  revealed,
  phoneWebhookEnabled,
  onReveal,
  onCopy,
}: PeopleProfileDrawerProps) {
  if (!person) return null;

  // Prefer revealed (post-enrichment) names when present — Apollo returns the
  // unmasked record on /people/match success.
  const firstName = revealed?.first_name || person.first_name || null;
  const lastNameUnmasked = revealed?.last_name && !revealed.last_name.includes("***") ? revealed.last_name : null;
  const lastNameMasked = person.last_name && person.last_name.includes("***") ? person.last_name : null;
  const lastName = lastNameUnmasked || person.last_name || lastNameMasked;
  const isStillMasked = !lastNameUnmasked && !!lastNameMasked;
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || person.full_name || "Unknown";
  const seedName = firstName || person.full_name || "?";
  const photoUrl = revealed?.photo_url || person.photo_url || null;

  const title = revealed?.title || person.title;
  const seniority = revealed?.seniority || person.seniority;
  const companyName = revealed?.company_name || person.company_name;
  const companyDomain = revealed?.company_domain || person.company_domain;
  const companyIndustry = revealed?.company_industry || person.company_industry;
  const companyEmployeeCount = revealed?.company_employee_count ?? person.company_employee_count;
  const headline = revealed?.headline || person.headline;
  const linkedinUrl = revealed?.linkedin_url || person.linkedin_url;
  const location = locationOf({
    city: revealed?.city || person.city,
    state: revealed?.state || person.state,
    country: revealed?.country || person.country,
  });

  const emailValue = state?.email ?? revealed?.email ?? null;
  const emailVerified = state?.emailVerified ?? revealed?.email_verified ?? false;
  const phoneValue = state?.phone ?? revealed?.phone ?? null;
  const phoneType = state?.phoneType ?? revealed?.phone_type ?? null;

  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      size={DrawerSize.SMALL}
      title="Person profile"
      icon="user"
      position="right"
      hasBackdrop
      canOutsideClickClose
      canEscapeKeyClose
    >
      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16, overflowY: "auto" }}>
        {/* ── Header ── */}
        <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt=""
              style={{ width: 60, height: 60, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: avatarColor(seedName),
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              {seedName.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 600, fontSize: 17, color: "#182026" }}>{fullName}</div>
              {isStillMasked && (
                <Tooltip content="Last name is hidden until you reveal email or phone. Both reveals return the unmasked profile from Apollo.">
                  <Tag minimal intent="warning" icon="lock" style={{ fontSize: 10 }}>masked</Tag>
                </Tooltip>
              )}
              {seniority && (
                <Tag minimal style={{ fontSize: 10, textTransform: "capitalize" }}>
                  {seniority.replace(/_/g, " ")}
                </Tag>
              )}
            </div>
            {(title || companyName) && (
              <div style={{ fontSize: 13, color: "#394b59", marginTop: 4 }}>
                {[title, companyName].filter(Boolean).join(" • ")}
              </div>
            )}
            {headline && headline !== title && (
              <div style={{ fontSize: 12, color: "#5c7080", marginTop: 6, fontStyle: "italic" }}>
                "{headline}"
              </div>
            )}
            {location && (
              <div style={{ fontSize: 12, color: "#5c7080", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                <Icon icon="geolocation" size={12} /> {location}
              </div>
            )}
          </div>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* ── Contact details ── */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#5c7080", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
            Contact details
          </div>

          {/* Email row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 0",
              borderBottom: "1px solid #ebf1f5",
            }}
          >
            <Icon icon="envelope" size={14} color="#5c7080" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Email
                {!emailValue && person.has_email_on_file && (
                  <span style={{ marginLeft: 6, color: "#0d8050" }}>· on file</span>
                )}
                {!emailValue && !person.has_email_on_file && (
                  <span style={{ marginLeft: 6, color: "#a7b6c2" }}>· not on file</span>
                )}
              </div>
              {emailValue ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <span
                    style={{ fontSize: 13, color: "#182026", cursor: "pointer" }}
                    onClick={() => onCopy(emailValue, "Email copied")}
                    title="Click to copy"
                  >
                    {emailValue}
                  </span>
                  {emailVerified && <Tag minimal intent="success" style={{ fontSize: 10 }}>verified</Tag>}
                  {state?.cached && <Tag minimal style={{ fontSize: 10 }}>cached</Tag>}
                </div>
              ) : state?.noResult?.kind === "email" ? (
                <div style={{ fontSize: 12, color: "#a7b6c2", marginTop: 2 }}>{state.noResult.message}</div>
              ) : (
                <div style={{ fontSize: 12, color: "#5c7080", marginTop: 2 }}>
                  Hidden until revealed — costs 1 credit if Apollo finds a verified address.
                </div>
              )}
            </div>
            {emailValue ? (
              <Button minimal small icon="clipboard" onClick={() => onCopy(emailValue, "Email copied")} />
            ) : (
              <Button
                small
                outlined
                intent={person.has_email_on_file ? "primary" : "none"}
                icon="envelope"
                loading={state?.emailLoading}
                onClick={() => onReveal("email")}
              >
                Reveal · 1 credit
              </Button>
            )}
          </div>

          {/* Phone row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 0",
              borderBottom: "1px solid #ebf1f5",
            }}
          >
            <Icon icon="phone" size={14} color="#5c7080" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#8a9ba8", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Mobile / direct phone
                {!phoneValue && person.has_phone_on_file && (
                  <span style={{ marginLeft: 6, color: "#0d8050" }}>· direct dial on file</span>
                )}
                {!phoneValue && !person.has_phone_on_file && (
                  <span style={{ marginLeft: 6, color: "#a7b6c2" }}>· no direct dial on file</span>
                )}
              </div>
              {phoneValue ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <span
                    style={{ fontSize: 13, color: "#182026", cursor: "pointer" }}
                    onClick={() => onCopy(phoneValue, "Phone copied")}
                    title="Click to copy"
                  >
                    {phoneValue}
                  </span>
                  {phoneType && (
                    <Tag minimal intent="success" style={{ fontSize: 10, textTransform: "capitalize" }}>
                      {phoneType}
                    </Tag>
                  )}
                  {state?.cached && <Tag minimal style={{ fontSize: 10 }}>cached</Tag>}
                </div>
              ) : state?.pending ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <Icon icon="time" size={11} color="#137cbd" />
                  <span style={{ fontSize: 12, color: "#137cbd" }}>
                    Verifying number… usually 1–3 min
                  </span>
                </div>
              ) : state?.noResult?.kind === "phone" ? (
                <div style={{ fontSize: 12, color: "#a7b6c2", marginTop: 2 }}>{state.noResult.message}</div>
              ) : (
                <div style={{ fontSize: 12, color: "#5c7080", marginTop: 2 }}>
                  Hidden until revealed — costs 5 credits if Apollo verifies a mobile or direct number.
                </div>
              )}
            </div>
            {phoneValue ? (
              <Button minimal small icon="clipboard" onClick={() => onCopy(phoneValue, "Phone copied")} />
            ) : !state?.pending && (
              <Tooltip
                disabled={phoneWebhookEnabled}
                content="Phone reveal is temporarily unavailable — verification webhook is not configured. Contact your admin."
              >
                <Button
                  small
                  outlined
                  icon="phone"
                  disabled={!phoneWebhookEnabled}
                  loading={state?.phoneLoading}
                  onClick={() => onReveal("phone")}
                >
                  Reveal · 5 credits
                </Button>
              </Tooltip>
            )}
          </div>

          {linkedinUrl && (
            <MetaRow
              icon="share"
              label="LinkedIn"
              value={linkedinUrl}
              onClick={() => window.open(linkedinUrl, "_blank", "noopener,noreferrer")}
              rightIcon="open-application"
            />
          )}
        </div>

        {(companyName || companyDomain || companyIndustry || companyEmployeeCount != null) && (
          <>
            <Divider style={{ margin: 0 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#5c7080", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                Employer
              </div>
              <MetaRow icon="office" label="Company" value={companyName ?? null} />
              <MetaRow
                icon="link"
                label="Domain"
                value={companyDomain ?? null}
                onClick={
                  companyDomain
                    ? () => window.open(`https://${companyDomain.replace(/^https?:\/\//, "")}`, "_blank", "noopener,noreferrer")
                    : undefined
                }
                rightIcon={companyDomain ? "open-application" : undefined}
              />
              <MetaRow icon="lightbulb" label="Industry" value={companyIndustry ?? null} />
              <MetaRow
                icon="people"
                label="Headcount"
                value={companyEmployeeCount != null ? `${companyEmployeeCount.toLocaleString()} employees` : null}
              />
            </div>
          </>
        )}

        {isStillMasked && !emailValue && !phoneValue && (
          <Callout intent="primary" icon="info-sign">
            Apollo hides last names in search results to protect their database.
            Revealing email <strong>or</strong> phone returns the full unmasked profile —
            you only pay credits when verified data is delivered.
          </Callout>
        )}
      </div>
    </Drawer>
  );
}
